import CACheckbox from '@ds/components/form-elements/CACheckbox'
import CAPagination from '@ds/components/navigation/CAPagination'
import CAIconComponent from '@ds/components/shared/CAIconComponent'
import CATable from '@ds/components/tables/CATable'
import CATableCell from '@ds/components/tables/CATableCell'
import type { CABadgeType } from '@ds/interfaces'
import { PER_PAGE } from '@vuejs/helpers/_constants'
import { defineComponent, type PropType } from 'vue'

import type { ActionElements } from '@/javascript/interfaces'
import { type PaginationData, paginator } from '@/javascript/lib/paginator'

import NoResultsFoundPlaceholder from '../common/NoResultsFoundPlaceholder'
import { stringToNumber } from '../packages/utils'
import DataTableActions from './DataTableActions'
import DataTableCell from './DataTableCell'
import LoadingIcon from './LoadingIcon'

export default defineComponent({
	name: 'DataTable',

	props: {
		additionalClass: { default: '', type: String },
		data: { required: true, type: Object as PropType<TableData> },
		dataTest: { default: '', type: String },
		isStickyHeader: { default: false, type: Boolean },
		itemsPerPage: { default: PER_PAGE.DEFAULT, type: Number },
		message: { default: '', type: String },
		modelValue: {
			required: false,
			type: Array as PropType<string[]>,
		},
		searchTerm: { default: '', type: String },
		showCheckboxes: { default: false, type: Boolean },
		showPagination: { default: false, type: Boolean },
		showProgressIndicator: { default: false, type: Boolean },
		showSelectAll: { default: false, type: Boolean },
		sortable: { default: false, type: Boolean },
		tableLabel: { default: '', type: String },
	},

	emits: {
		action: (_action: string, _id?: number | string) => true,
		selected: (_ids: string[]) => true,
	},

	data(): Data {
		return {
			currentPage: 1,
			perPage: PER_PAGE.ITEMS,
			selectedIds: [],
			sortByColumnId: this.data.columns[0].columnId,
			sortOrder: 'ascending',
		}
	},

	computed: {
		colspan(): number {
			return this.showCheckboxes ? this.data.columns.length + 1 : this.data.columns.length
		},

		filteredItems(): RowData[] {
			const term = this.searchTerm.toLowerCase()
			const items = !!this.searchTerm ?
				[...this.data.rows].filter(row => row.cells.some(cell => cell.title.text.toLowerCase().includes(term))) :
				[...this.data.rows]

			if (this.sortable) {
				const index = this.data.columns.findIndex(c => c.columnId === this.sortByColumnId) ?? 0

				items.sort((a, b) => {
					const columnA = a.cells[index].title.value ?? a.cells[index].title.text
					const columnB = b.cells[index].title.value ?? b.cells[index].title.text

					// Attempt to convert both cell values to numbers
					const numA = parseFloat(columnA.replace(/%/g, ''))
					const numB = parseFloat(columnB.replace(/%/g, ''))

					if (!isNaN(numA) && !isNaN(numB)) {
						// Both columnA and columnB are valid numbers, compare numerically
						return this.sortOrder === 'ascending' ? numA - numB : numB - numA
					} else {
						// At least one of columnA or columnB is not a number, compare as strings
						return this.sortOrder === 'ascending' ? columnA?.localeCompare(columnB) : columnB?.localeCompare(columnA)
					}

				})
			}

			return items
		},

		hasAllRowsSelected(): boolean {
			return this.selectedIds.length === this.rowIds.length
		},

		hasRows(): boolean {
			return this.data.rows.length > 0
		},

		isFirstPage(): boolean {
			return this.pagination.page === 1
		},

		isLastPage(): boolean {
			return this.pagination.page === this.pagination.totalPages
		},

		paginatedItems(): RowData[] {
			return this.pagination.data
		},

		pagination(): PaginationData<RowData> {
			return paginator(this.filteredItems, this.currentPage, this.perPage)
		},

		rowIds(): string[] {
			return this.data.rows.map(i => i.rowId)
		},
	},

	watch: {
		searchTerm() {
			this.currentPage = 1
		},

		modelValue() {
			if (this.modelValue) {
				this.selectedIds = this.modelValue
			}
		},
	},

	mounted() {
		this.perPage = this.itemsPerPage || this.perPage
	},

	methods: {
		goToPage(page: number) {
			this.currentPage = page
		},

		handleCheckbox(options: SelectedOptions) {
			this.$nextTick(() => {
				if (options.all) {
					this.selectedIds = this.hasAllRowsSelected ? [] : this.rowIds
				} else {
					if (this.selectedIds.includes(options.id)) {
						this.selectedIds = this.selectedIds.filter(i => i !== options.id)
					} else {
						this.selectedIds.push(options.id)
					}
				}

				this.$emit('selected', this.selectedIds)
			})
		},

		handleClick(action: string, id?: number | string) {
			const rowAction = action ?? ''

			this.$emit('action', rowAction, id && typeof id === 'string' ? stringToNumber(id) : id)
		},

		isSelected(id: string): boolean {
			return this.selectedIds.includes(id)
		},

		onChangePage(direction: string, event?: Event) {
			event?.preventDefault()
			switch (direction) {
				case 'first':
					this.currentPage = 1
					break

				case 'last':
					this.currentPage = this.pagination.totalPages
					break

				case 'next':
					this.currentPage = this.pagination.nextPage ?? this.pagination.totalPages
					break

				case 'previous':
					this.currentPage = this.pagination.previousPage ?? 1
					break
			}
		},

		sortBy(columnId: string, event: Event) {
			event.preventDefault()
			if (this.sortByColumnId === columnId) {
				this.sortOrder = this.sortOrder === 'ascending' ? 'descending' : 'ascending'
			} else {
				this.sortByColumnId = columnId
				this.sortOrder = 'ascending'
			}
		},
	},

	render() {
		return (
			<div>
				<CATable
					class={{ 'ca-table__sticky-header': this.isStickyHeader, [this.additionalClass]: !!this.additionalClass }}
					data-test={this.dataTest}
				>
					{{
						tableHeader: () =>
							<thead class="ca-table__table-header" data-test="ca-table-header">
								<tr>
									{this.showCheckboxes &&
										<th data-test="select-all-checkbox-wrapper" scope="col">
											<CACheckbox
												dataTest="select-all-checkboxes"
												id="select-all-checkboxes"
												modelValue={this.hasAllRowsSelected}
												onUpdate:modelValue={() => this.handleCheckbox({ all: true, id: '' })}
											/>
										</th>
									}

									{this.data.columns.map(column =>
										<th
											class={{ 'cursor-pointer': column.isSortable }}
											data-column-id={column.columnId}
											scope="col"
											style={column.width ? `width: ${column.width}` : 'auto'}
											v-tooltip={column.tooltip}
											onClick={e => column.isSortable && column.columnId ? this.sortBy(column.columnId, e) : 'javascript:void(0)'}
										>
											<div class="ca-table__sort">
												<span>{String(column.name)}</span>
												{column.isSortable && <CAIconComponent icon="SVGSelector" />}
											</div>
										</th>
									)}
								</tr>
							</thead>,

						tableRows: () =>
							<>
								{this.paginatedItems.map(row =>
									<tr aria-selected={this.isSelected(row.rowId)} data-row-id={row.rowId}>
										{this.showCheckboxes &&
											<CATableCell>
												<CACheckbox
													dataTest="data-table-checkbox"
													id={`row-${row.rowId}`}
													modelValue={this.isSelected(row.rowId)}
													onUpdate:modelValue={() => this.handleCheckbox({ all: false, id: row.rowId })}
												/>
											</CATableCell>
										}

										{row.cells?.map((cell, index) =>
											<DataTableCell
												cell={cell}
												index={index}
												width={this.data.columns[index]?.width}
											/>
										)}

										{row.actions &&
											<CATableCell>
												{row.actions.length > 1 ?
													<DataTableActions
														actions={row.actions}
														id={row.rowId}
														onAction={(action: string, id?: number | string) => this.$emit('action', action, id)}
													/>

													:

													row.actions.length === 1 ?
														<a
															class={`custom-menu__control-button, ${row.actions[0].class}`}
															data-test={row.actions[0].dataTest}
															href={row.actions[0].link ? row.actions[0].link : 'javascript:void(0)'}
															v-tooltip={row.actions[0].tooltip}
															onClick={() => row.actions && row.actions[0].text ?
																this.handleClick(row.actions[0].text, row.rowId) : undefined}
														>
															<CAIconComponent icon={row.actions[0].icon} />
														</a>

														:

														null
												}
											</CATableCell>
										}
									</tr>
								)}

								{!this.hasRows &&
									<tr>
										<CATableCell columnSpan={this.colspan}>
											<NoResultsFoundPlaceholder
												showButton={false}
												text={this.message}
												useActionSlot={false}
											/>
										</CATableCell>
									</tr>
								}
							</>,
					}}
				</CATable>

				{this.$slots.pagination?.()}

				{this.filteredItems.length > this.itemsPerPage && this.showPagination &&
					<CAPagination
						itemsPerPage={this.itemsPerPage}
						numberOfRows={this.filteredItems.length}
						v-model={this.currentPage}
						onUpdate:modelValue={this.goToPage}
					/>
				}

				{this.showProgressIndicator &&
					<div class="horizontal-vertical-center mt-16">
						<LoadingIcon size="medium" />
					</div>
				}
			</div>
		)
	},
})

interface Data {
	currentPage: number
	perPage: number
	selectedIds: string[]
	sortByColumnId: string | undefined
	sortOrder: 'ascending' | 'descending' | 'none' | 'other' | undefined
}

export interface Cell {
	badge?: CABadgeType
	details?: CellAttributes
	title: CellAttributes
}

export interface CellAttributes {
	class?: string
	dataTest?: string
	dataTestName?: string
	link?: string
	text: string
	value?: string
	tooltip?: string
}

export interface ColumnData {
	ariaLabel: string
	columnId?: string
	isSortable?: boolean
	name: number | string
	tooltip?: string
	width?: string
}

export interface RowData {
	actions?: ActionElements[]
	cells: Cell[]
	rowId: string
}

interface SelectedOptions {
	all: boolean
	id: string
}

export interface TableData {
	columns: ColumnData[]
	rows: RowData[]
}

export type SortOrder
	= ''
	| 'ascending'
	| 'descending'
	| 'none'
